import { Dialog } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import { getNFTRedeemValues } from 'src/types/nft.ts';
import { environment } from 'src/config';
import { PrimaryButton } from 'src/components/Button/PrimaryButton';
import { NFTRedeemCalculator } from 'src/utils/NFTRedeemCalculator.ts';
import { epochToDate } from 'src/utils/epochUtils';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { sendRedeemTransaction } from 'src/services/redeemService.ts';
import style from './NFTTimelineDialog.module.scss';
import { useGetAccountInfo } from '@multiversx/sdk-dapp/hooks';
import { ReactComponent as RightArrowIcon } from 'src/assets/icons/arrow-right.svg';
import { ReactComponent as ExternalLinkIcon } from 'src/assets/icons/arrow-up-right.svg';

function NFTTimelineDialog({ open, onClose, nft, currentEpoch, values, is_explorer = false, explorerUrl }) {
    const [isRedeeming, setIsRedeeming] = useState(false);
    const { account } = useGetAccountInfo();

    // Ownership check: the connected wallet must hold this exact NFT to claim it.
    // In the normal (non-explorer) flow the NFT comes from the user's own list,
    // so it is owned by definition. In the explorer/search flow we must compare
    // the searched NFT's owner with the connected address.
    const isOwner = !!account?.address && !!nft?.owner && nft.owner === account.address;
    const canRedeem = !is_explorer || isOwner;

    // Memoize parsed attributes to prevent recalculation on every render
    const parsedAttributes = useMemo(() => 
        NFTRedeemCalculator.parseAttributes(nft.attributes),
        [nft.attributes]
    );

    // Memoize timeline data calculations
    const timelineData = useMemo(() => {
        // Calculate points between initial epoch and final epoch
        const points = Array.from({ length: 11 }, (_, i) => {
            // For the last point, use finalEpoch directly
            const epochAtPoint = i === 10 
                ? parsedAttributes.finalEpoch 
                : parsedAttributes.startEpoch + (i * Math.floor((parsedAttributes.finalEpoch - parsedAttributes.startEpoch) / 10));
            const redeemValues = getNFTRedeemValues(nft, epochAtPoint);
            const date = epochToDate(epochAtPoint, currentEpoch, environment === 'devnet');
            
            return {
                epoch: epochAtPoint,
                value: redeemValues.currentValue,
                label: date.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit'
                })
            };
        });

        // Add current epoch point if it's between start and final
        if (currentEpoch > parsedAttributes.startEpoch && currentEpoch < parsedAttributes.finalEpoch) {
            const currentDate = epochToDate(currentEpoch, currentEpoch, environment === 'devnet');
            const currentPoint = {
                epoch: currentEpoch,
                value: values.currentValue,
                label: currentDate.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit'
                }),
                isCurrent: true
            };
            
            // Insert current point at the correct position
            const insertIndex = points.findIndex(point => point.epoch > currentEpoch);
            if (insertIndex !== -1) {
                points.splice(insertIndex, 0, currentPoint);
            } else {
                points.push(currentPoint);
            }
        }

        return points;
    }, [parsedAttributes, nft, currentEpoch, values]);

    const handleRedeem = async () => {
        // Guard: never attempt a claim if the connected wallet does not own the NFT.
        if (!canRedeem) {
            return;
        }
        try {
            setIsRedeeming(true);
            // Extract collection and nonce from NFT identifier (format: TADAVR-1f47d8-14)
            const parts = nft.identifier.split('-');
            const collection = parts.slice(0, -1).join('-'); // Everything except the last part
            const nonceStr = parts[parts.length - 1];
            
            console.log('Redeeming NFT:', {
                identifier: nft.identifier,
                collection,
                nonce: parseInt(nonceStr, 16),
                senderAddress: account.address
            });

            await sendRedeemTransaction(
                {
                    collection,
                    nonce: parseInt(nonceStr, 16)  // Convert hex nonce to number
                },
                account.address
            );
            onClose();
        } catch (error) {
            console.error('Error redeeming NFT:', error);
        } finally {
            setIsRedeeming(false);
        }
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const isCurrentEpoch = payload[0].payload.isCurrent;
            return (
                <div className={style.customTooltip}>
                    <p className={style.value}>{payload[0].value?.toFixed(3)} TADA</p>
                    <p className={style.date}>
                        {isCurrentEpoch ? 'Today' : label}
                    </p>
                    <p className={style.epoch}>Epoch: {payload[0].payload.epoch}</p>
                </div>
            );
        }
        return null;
    };

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            classes={{ paper: style.dialog }}
        >
            <div className={style.header}>
                <h2>{nft.identifier} Timeline</h2>
                <div className={style.actions}>
                    {is_explorer && (
                        <a
                            href={`${explorerUrl}/transactions`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={style.explorerButton}
                        >
                            Discover on Explorer <ExternalLinkIcon />
                        </a>
                    )}
                    {canRedeem ? (
                        <PrimaryButton
                            text={isRedeeming ? "Redeeming..." : "Redeem"}
                            onClick={handleRedeem}
                            fluid={false}
                            disabled={isRedeeming}
                            endIcon={<RightArrowIcon />}
                        />
                    ) : (
                        <span
                            title={
                                account?.address
                                    ? "You don't own this T-Bondz, so you can't claim it."
                                    : 'Connect the wallet that owns this T-Bondz to claim it.'
                            }
                            style={{ display: 'inline-flex', cursor: 'not-allowed' }}
                        >
                            <PrimaryButton
                                text="Redeem"
                                onClick={() => {}}
                                fluid={false}
                                disabled={true}
                                endIcon={<RightArrowIcon />}
                                backgroundColor="hsl(0, 0%, 35%)"
                                textColor="hsl(0, 0%, 70%)"
                            />
                        </span>
                    )}
                    <button className={style.closeButton} onClick={onClose}>
                        <CloseIcon />
                    </button>
                </div>
            </div>

            <div className={style.content}>
                <div className={style.chartContainer}>
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={timelineData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="label"
                                interval={1}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                            />
                            <YAxis />
                            <Tooltip content={<CustomTooltip />} />
                            <Line 
                                type="monotone" 
                                dataKey="value" 
                                stroke="hsl(285, 100%, 58%)"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 6, fill: "hsl(285, 100%, 58%)", stroke: "white", strokeWidth: 2 }}
                            />
                            {currentEpoch > parsedAttributes.startEpoch && currentEpoch < parsedAttributes.finalEpoch && (
                                <ReferenceDot
                                    x={epochToDate(currentEpoch, currentEpoch, environment === 'devnet').toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: '2-digit'
                                    })}
                                    y={values.currentValue}
                                    r={6}
                                    fill="hsl(285, 100%, 58%)"
                                    stroke="white"
                                    strokeWidth={2}
                                >
                                    <label 
                                        position="top" 
                                        offset={15} 
                                        fill="hsl(285, 100%, 58%)"
                                        fontSize="12px"
                                        fontWeight="bold"
                                    >
                                        Today
                                    </label>
                                </ReferenceDot>
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <div className={style.highlights}>
                    <div className={style.highlight}>
                        <span>Current Value</span>
                        <strong>{values.currentValue?.toFixed(3)} TADA</strong>
                    </div>
                    <div className={style.highlight}>
                        <span>Unlock Date</span>
                        <strong>{timelineData[timelineData.length - 1]?.label}</strong>
                    </div>
                    <div className={style.highlight}>
                        <span>Maximum Value</span>
                        <strong>{(values.maxValue / Math.pow(10, 18))?.toFixed(3)} TADA</strong>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

export default NFTTimelineDialog; 